import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { collection, getDocs, getFirestore } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const convertedStatuses = ['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped']
const notConvertedStatuses = ['inactive', 'Did Not Join']

const convertedStatusSet = new Set(convertedStatuses.map((value) => String(value).trim().toLowerCase()))
const notConvertedStatusSet = new Set(notConvertedStatuses.map((value) => String(value).trim().toLowerCase()))

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const formatPercent = (value) => {
  const safeValue = Number.isFinite(value) ? value : 0
  return `${safeValue.toFixed(1)}%`
}

const normalizeText = (value) => String(value || '').trim()
const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const ConversionTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="conversionBySourceTooltip">
      <strong>{label}</strong>
      <div className="conversionBySourceTooltipRow">
        <span>Applicants</span>
        <strong>{formatCount(row.applicants)}</strong>
      </div>
      <div className="conversionBySourceTooltipRow">
        <span>Converted</span>
        <strong>{formatCount(row.converted)}</strong>
      </div>
      <div className="conversionBySourceTooltipRow">
        <span>Not converted</span>
        <strong>{formatCount(row.notConverted)}</strong>
      </div>
      <div className="conversionBySourceTooltipRow">
        <span>Conversion rate</span>
        <strong>{formatPercent(row.conversionRate)}</strong>
      </div>
    </div>
  )
}

function ConvBySource() {
  const db = getFirestore(app)
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const querySnapshot = await getDocs(collection(db, 'users'))

      const normalizedRecords = []

      querySnapshot.forEach((docItem) => {
        const data = docItem.data()
        const createdAt = data.appDate?.toDate ? data.appDate.toDate() : null

        normalizedRecords.push({
          source: normalizeText(data.hear) || 'Unknown',
          status: normalizeStatus(data.userStatus) || 'inactive',
          recordDateMs: createdAt ? createdAt.getTime() : null
        })
      })

      setRecords(normalizedRecords)
    } catch (fetchError) {
      console.error('Failed to load conversion by source analysis:', fetchError)
      setRecords([])
      setError('We could not load the conversion analysis right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRecords = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()
    const startMs = startDate ? new Date(`${startDate}T00:00:00.000`).getTime() : null
    const endMs = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null

    return records.filter((record) => {
      if (search && !record.source.toLowerCase().includes(search)) {
        return false
      }

      if ((startMs !== null || endMs !== null) && record.recordDateMs === null) {
        return false
      }

      if (startMs !== null && record.recordDateMs !== null && record.recordDateMs < startMs) {
        return false
      }

      if (endMs !== null && record.recordDateMs !== null && record.recordDateMs > endMs) {
        return false
      }

      return true
    })
  }, [records, searchTerm, startDate, endDate])

  const sourceRows = useMemo(() => {
    const rows = new Map()

    filteredRecords.forEach((record) => {
      if (!rows.has(record.source)) {
        rows.set(record.source, {
          source: record.source,
          applicants: 0,
          converted: 0,
          notConverted: 0,
          active: 0,
          graduated: 0,
          neverJoined: 0,
          deferred: 0,
          expelled: 0,
          dropped: 0
        })
      }

      const bucket = rows.get(record.source)

      if (record.status === 'inactive') {
        bucket.applicants += 1
      } else if (record.status === 'student') {
        bucket.active += 1
        bucket.converted += 1
      } else if (record.status === 'completed') {
        bucket.graduated += 1
        bucket.converted += 1
      } else if (record.status === 'did not join') {
        bucket.neverJoined += 1
        bucket.notConverted += 1
      } else if (record.status === 'deferred') {
        bucket.deferred += 1
        bucket.converted += 1
      } else if (record.status === 'expelled') {
        bucket.expelled += 1
        bucket.converted += 1
      } else if (record.status === 'dropped') {
        bucket.dropped += 1
        bucket.converted += 1
      } else if (convertedStatusSet.has(record.status)) {
        bucket.converted += 1
      } else if (notConvertedStatusSet.has(record.status)) {
        bucket.notConverted += 1
      } else {
        bucket.notConverted += 1
      }
    })

    return Array.from(rows.values())
      .map((row) => {
        const total = row.applicants + row.converted + row.notConverted

        return {
          ...row,
          total,
          conversionRate: total > 0 ? (row.converted / total) * 100 : 0
        }
      })
      .filter((row) => row.total > 0)
      .sort((first, second) => {
        if (second.total !== first.total) {
          return second.total - first.total
        }

        return first.source.localeCompare(second.source)
      })
  }, [filteredRecords])

  const chartData = useMemo(() => sourceRows.slice(0, 8), [sourceRows])

  const stats = useMemo(() => {
    return sourceRows.reduce(
      (accumulator, row) => {
        accumulator.total += row.total
        accumulator.applicants += row.applicants
        accumulator.converted += row.converted
        accumulator.notConverted += row.notConverted
        return accumulator
      },
      { total: 0, applicants: 0, converted: 0, notConverted: 0 }
    )
  }, [sourceRows])

  const topSource = sourceRows[0] || null
  const topConversionSource = useMemo(() => {
    return [...sourceRows].sort((first, second) => second.conversionRate - first.conversionRate)[0] || null
  }, [sourceRows])
  const topVolumeSource = useMemo(() => {
    return [...sourceRows].sort((first, second) => second.total - first.total)[0] || null
  }, [sourceRows])
  const overallConversionRate = stats.total > 0 ? (stats.converted / stats.total) * 100 : 0
  const hasRows = sourceRows.length > 0
  const hasChartData = chartData.length > 0
  const visibleCount = filteredRecords.length
  const chartSummary = chartData.length
    ? `${chartData.slice(0, 5).map((row) => row.source).join(', ')}${chartData.length > 5 ? ' + others' : ''}`
    : 'No series available'
  const emptyMessage = records.length
    ? 'No sources match the current filters.'
    : 'No records were found yet.'

  return (
    <div className="mainContainer conversionBySourcePage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="conversionBySourceContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Conversion by Source</h4>
              <p className="appsSubtext">
                Compare conversion strength across sources and quickly spot where applicants are joining or dropping off.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total records</span>
                <strong>{formatCount(stats.total)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Converted</span>
                <strong>{formatCount(stats.converted)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Not converted</span>
                <strong>{formatCount(stats.notConverted)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Conversion rate</span>
                <strong>{formatPercent(overallConversionRate)}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard conversionBySourceFiltersCard">
            <div className="appsSearch input-group conversionBySourceSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search source..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="conversionBySourceDateGroup">
              <div className="conversionBySourceDateControl">
                <span>From</span>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>
              <div className="conversionBySourceDateControl">
                <span>To</span>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
            </div>

            <div className="conversionBySourceFiltersMeta">
              <span>{formatCount(visibleCount)} records visible</span>
              <span>{formatCount(chartData.length)} sources shown in chart</span>
            </div>

            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="conversionBySourceSummaryGrid">
            <div className="conversionBySourceSummaryCard">
              <span>Leading source</span>
              <strong>{topSource ? topSource.source : '—'}</strong>
              <p>{topSource ? `${formatCount(topSource.total)} tracked records` : 'No data yet.'}</p>
            </div>
            <div className="conversionBySourceSummaryCard">
              <span>Best conversion</span>
              <strong>{topConversionSource ? topConversionSource.source : '—'}</strong>
              <p>{topConversionSource ? formatPercent(topConversionSource.conversionRate) : 'No data yet.'}</p>
            </div>
            <div className="conversionBySourceSummaryCard">
              <span>Converted</span>
              <strong>{formatCount(stats.converted)}</strong>
              <p>Students who moved beyond the applicant stage.</p>
            </div>
            <div className="conversionBySourceSummaryCard">
              <span>Drop-offs</span>
              <strong>{formatCount(stats.notConverted)}</strong>
              <p>Sources with inactive or not-joined outcomes.</p>
            </div>
          </div>

          <div className="appsTableCard conversionBySourceChartCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                Source conversion mix
              </h6>
              <span>{chartSummary}</span>
            </div>

            <div className="conversionBySourceChartWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading conversion analysis...</span>
                  </Spinner>
                  <p>Loading conversion analysis...</p>
                </div>
              ) : hasChartData ? (
                <div className="conversionBySourceChartBox">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                      <XAxis
                        dataKey="source"
                        tick={{ fontSize: 12, fill: '#475569' }}
                        axisLine={{ stroke: '#cbd5e1' }}
                        tickLine={false}
                        interval={0}
                        angle={-18}
                        textAnchor="end"
                        height={72}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 12, fill: '#64748b' }}
                        axisLine={{ stroke: '#cbd5e1' }}
                        tickLine={false}
                      />
                      <Tooltip content={<ConversionTooltip />} />
                      <Legend
                        verticalAlign="top"
                        height={32}
                        iconType="circle"
                        wrapperStyle={{ fontSize: '12px', color: '#64748b' }}
                      />
                      <Bar dataKey="applicants" stackId="source" name="Applicants" fill="#94a3b8" />
                      <Bar dataKey="converted" stackId="source" name="Converted" fill="#0f766e" />
                      <Bar dataKey="notConverted" stackId="source" name="Not converted" fill="#ef4444" radius={[0, 10, 10, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="incomeStateCard">
                  <p>{emptyMessage}</p>
                </div>
              )}
            </div>
          </div>

          <div className="appsInsightsCard conversionBySourceInsightsCard">
            <div className="appsInsightsHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Conversion highlights
              </h6>
              <span>{formatCount(sourceRows.length)} sources tracked</span>
            </div>

            <div className="conversionBySourceHighlightsGrid">
              <div className="conversionBySourceMiniStats">
                <div className="conversionBySourceMiniStat">
                  <span>Top source</span>
                  <strong>{topVolumeSource ? topVolumeSource.source : '—'}</strong>
                  <small>{topVolumeSource ? formatCount(topVolumeSource.total) : 'No data'}</small>
                </div>
                <div className="conversionBySourceMiniStat">
                  <span>Best conversion</span>
                  <strong>{topConversionSource ? formatPercent(topConversionSource.conversionRate) : '0.0%'}</strong>
                  <small>{topConversionSource ? topConversionSource.source : 'No data'}</small>
                </div>
              </div>

              <div className="appsInsightsList">
                {sourceRows.length ? (
                  sourceRows.slice(0, 5).map((row) => (
                    <div className="appsInsightsItem" key={row.source}>
                      <div>
                        <strong>{row.source}</strong>
                        <span>{formatCount(row.total)} tracked records</span>
                      </div>
                      <small>{formatPercent(row.conversionRate)} converted</small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <p>No source highlights match the current data.</p>
                  </div>
                )}
              </div>
            </div>

            <p className="appsInsightsMore">
              {topConversionSource
                ? `Best conversion source: ${topConversionSource.source} at ${formatPercent(topConversionSource.conversionRate)}.`
                : 'Refresh after new records are added to update the highlights.'}
            </p>
          </div>

          <div className="appsTableCard conversionBySourceTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Source breakdown
              </h6>
              <span>{formatCount(sourceRows.length)} results</span>
            </div>

            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading conversion summary...</span>
                  </Spinner>
                  <p>Loading conversion summary...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable conversionBySourceTable">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Applicants</th>
                      <th>Converted</th>
                      <th>Not Converted</th>
                      <th>Total</th>
                      <th>Conversion Rate</th>
                      <th>Mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasRows ? (
                      sourceRows.map((row) => {
                        const share = stats.total > 0 ? (row.total / stats.total) * 100 : 0
                        const topTotal = sourceRows[0]?.total || 0
                        const progress = topTotal > 0 ? (row.total / topTotal) * 100 : 0

                        return (
                          <tr key={row.source}>
                            <td>
                              <div className="conversionBySourceRowLabel">
                                <strong>{row.source}</strong>
                                <span>{row.total > 0 ? 'Tracked source' : 'No records'}</span>
                              </div>
                            </td>
                            <td>{formatCount(row.applicants)}</td>
                            <td>{formatCount(row.converted)}</td>
                            <td>{formatCount(row.notConverted)}</td>
                            <td>
                              <span className="conversionBySourceTotalCell">{formatCount(row.total)}</span>
                            </td>
                            <td>{formatPercent(row.conversionRate)}</td>
                            <td>
                              <div className="conversionBySourceMixCell">
                                <div className="conversionBySourceMixMeta">
                                  <span>{formatPercent(share)}</span>
                                  <small>share of total</small>
                                </div>
                                <div className="conversionBySourceTrack" aria-hidden="true">
                                  <span
                                    className="conversionBySourceTrackFill"
                                    style={{ width: `${Math.min(progress, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConvBySource
